import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import MarkUpText from '@/components/MarkUpText/MarkUpText';
import solicitationNotifications, {
  selectedSolicitationAtom,
  solicitationModalMode,
} from '../../utils/solicitation-notification-helpers';
import {
  GET_SOLICITATIONS,
  DELETE_SOLICITATION,
  GET_MULTIPLE_OPTIONS,
} from '../../queries';
import { pageErrorNotifications } from '../../../../utilities/error-handling';

export default function useSolicitation(onCompleteCallback) {
  const [solicitations, setSolicitations] = useState([]);
  const [aacCodes, setAacCodes] = useState([]);
  const [, setSolicitationNotification] = useRecoilState(
    solicitationNotifications,
  );

  const [selectedSolicitationRow, setSelectedSolicitationRow] = useRecoilState(
    selectedSolicitationAtom,
  );

  const [modalMode, setModalMode] = useRecoilState(solicitationModalMode);
  const location = useLocation();

  useEffect(() => {
    // keep notifications with showOnHomePage flag
    setSolicitationNotification((prev) =>
      prev.filter((item) => item.showOnHomePage),
    );
  }, []);

  useEffect(() => {
    // Clear all notifications when navigating away from solicitation pages
    if (!location.pathname.includes('/solicitations')) {
      setSolicitationNotification([]);
    }
  }, [location.pathname, setSolicitationNotification]);

  const [getSolicitations] = useLazyQuery(GET_SOLICITATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setSolicitations(data.getSolicitations);
    },
  });

  useQuery(GET_MULTIPLE_OPTIONS, {
    fetchPolicy: 'cache-first',
    variables: {
      skipYears: true,
      options: [
        {
          model: 'StandardsCodeModel',
          label: 'code',
          value: 'code',
          filter: {
            operator: 'EQ',
            key: 'category',
            value: 'Activity Address Code',
          },
          uniqueKey: 'aac',
          includeValueInLabel: false,
        },
      ],
    },
    onCompleted: (data) => {
      if (data.getMultipleOptions)
        setAacCodes(data.getMultipleOptions.map(({ value }) => value));
    },
  });

  const [deleteSolicitation] = useMutation(DELETE_SOLICITATION, {
    onError: (requestError) => {
      setSolicitationNotification([
        ...pageErrorNotifications(requestError, {
          id: 'DELETE_SOLICITATION_ERROR',
          message: requestError.message,
          type: 'error',
          closeable: false,
          showInModal: false,
        }),
      ]);
    },
    onCompleted: (response) => {
      const { id, solicitationNumber } = response.deleteSolicitationById || {};
      if (id) {
        const message = `Solicitation ${solicitationNumber} has been successfully deleted.`;
        setSolicitationNotification([
          {
            id: 'DELETE_SOLICITATION',
            message: (
              <MarkUpText
                content={message}
                textToMarkUp={`Solicitation ${solicitationNumber}`}
                marker="strong"
              />
            ),
            type: 'success',
            closeable: true,
            showInModal: false,
          },
        ]);
        if (onCompleteCallback) onCompleteCallback();
      }
    },
  });

  return {
    modalMode,
    setModalMode,
    selectedSolicitationRow,
    setSelectedSolicitationRow,
    solicitations,
    setSolicitations,
    getSolicitations,
    deleteSolicitation,
    aacCodes,
  };
}
